<template>
  <v-card>
    <slot name="header" />
    <Loader v-if="isLoading" />
    <div v-else>
      <v-card-text class="mt-1 mb-0 pb-0">
        <div>
          <info-box
            v-if="!sentCountsStatisticsLoading && !emailSentCountsStatisticsForCompany"
            :info-message="t('dashboard.details.no-data')"
          />
          <div
            class="d-md-flex justify-space-between card-text-wrapper"
            v-else
          >
            <div style="display: grid">
              <Info
                :number="emailSentCountsStatisticsForCompany?.sent?.toLocaleString() || 0"
                :description="t('dashboard.details.email-funnels.description')"
              />
              <Info
                v-if="!dashboardContext.hasFiltersApplied.value"
                :number="
                  emailSentCountsStatisticsForCompany?.lastThirtyDaysSent?.toLocaleString() || 0
                "
                :description="t('dashboard.details.email-funnels.description-last-thirty-days')"
              />
            </div>
            <BarChart
              :config="chartConfig"
              :series="chartSeries"
            />
          </div>
        </div>
      </v-card-text>
      <v-card-text class="mt-1 mb-0 pb-0">
        <div>
          <info-box
            v-if="!bounceStatisticsLoading && !emailBounceStatisticsForCompany"
            :info-message="t('dashboard.details.no-data')"
          />
          <div
            class="d-md-flex justify-space-between card-text-wrapper"
            v-else
          >
            <div class="apex-chart-info">
              <div>
                <Info
                  v-if="lastSelectedDate"
                  :number="(totalSoftBounces + totalHardBounces)?.toLocaleString() || '0'"
                  :description="
                    t('dashboard.details.total-bounces.description-date') +
                    ': ' +
                    formatDate(lastSelectedDate, 'namedWeekdayWithYear', $i18n.locale)
                  "
                />
                <Info
                  v-else
                  :number="totalBounces?.toLocaleString() || '0'"
                  :description="t('dashboard.details.total-bounces.description')"
                />
              </div>

              <div>
                <Info
                  v-if="lastSelectedDate"
                  :number="totalAutoReplies?.toLocaleString() || '0'"
                  :description="
                    t('dashboard.details.auto-reply.description-date') +
                    ': ' +
                    formatDate(lastSelectedDate, 'namedWeekdayWithYear', $i18n.locale)
                  "
                />
                <Info
                  v-else
                  :number="totalAutoReplies?.toLocaleString() || '0'"
                  :description="t('dashboard.details.auto-reply.description')"
                />
              </div>
            </div>

            <div class="apex-chart">
              <ApexChart
                type="pie"
                height="200"
                :options="pieChartConfig"
                :series="pieChartSeries"
              />
            </div>
          </div>
        </div>
      </v-card-text>
    </div>
    <slot name="footer" />
  </v-card>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import type { ApexOptions } from 'apexcharts'
import { computed, inject, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import ApexChart from 'vue3-apexcharts'

import InfoBox from '@/components/InfoBox.vue'
import { formatDate } from '@/lib/dateTime'
import { createBarChartConfig } from '@/modules/dashboard/dashboardDetails/lib/barChartConfig'
import { createBarChartSeries } from '@/modules/dashboard/dashboardDetails/lib/barChartSeries'
import BarChart from '@/modules/dashboard/dashboardDetails/shared/barChart.vue'
import Info from '@/modules/dashboard/dashboardDetails/shared/info.vue'
import Loader from '@/modules/dashboard/dashboardDetails/shared/loader.vue'
import EMAIL_FUNNELS_AUTO_REPLY_STATISTICS from '@/modules/statistics/GetCompanyAutoReply.gql'
import EMAIL_FUNNELS_BOUNCE_STATISTICS from '@/modules/statistics/GetCompanyEmailFunnelsBounceStatistics.gql'
import EMAIL_FUNNELS_SENT_COUNTS_STATISTICS from '@/modules/statistics/GetCompanyEmailFunnelsSentCounts.gql'

import {
  DashboardContext,
  dashboardContextDefault,
  DashboardContextSymbol,
} from '../dashboardContext'

const { t } = useI18n()
const route = useRoute()
const isMasterView = computed(() => route.path.startsWith('/master'))
const routeParamId = computed(() => route?.params?.id || null)

const dashboardContext = inject<DashboardContext>(DashboardContextSymbol, dashboardContextDefault)

const variables = reactive({
  input: {
    lastThirtyDays: false,
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value,
    ...dashboardContext.formattedDateFilters.value,
  },
})

const { result: emailBounceStatistics, loading: bounceStatisticsLoading } = useQuery(
  EMAIL_FUNNELS_BOUNCE_STATISTICS,
  variables
)

const emailBounceStatisticsForCompany = computed(
  () => emailBounceStatistics.value?.emailBounceStatistics
)
const totalBounces = computed(() =>
  !emailBounceStatistics.value?.emailBounceStatistics
    ? 0
    : emailBounceStatisticsForCompany.value?.softBounce +
      emailBounceStatisticsForCompany.value?.hardBounce
)

const totalSoftBounces = ref(0)
const totalHardBounces = ref(0)
const lastSelectedDate = ref(null)

watch(
  emailBounceStatisticsForCompany,
  (val) => {
    if (val) {
      totalSoftBounces.value = val.softBounce ?? 0
      totalHardBounces.value = val.hardBounce ?? 0
    }
  },
  { immediate: true }
)

const { result: emailSentCountsStatistics, loading: sentCountsStatisticsLoading } = useQuery(
  EMAIL_FUNNELS_SENT_COUNTS_STATISTICS,
  variables
)

const emailSentCountsStatisticsForCompany = computed(
  () => emailSentCountsStatistics.value?.emailSentCountsStatistics
)

const { result: emailAutoReplyStatistics, loading: autoReplyStatisticsLoading } = useQuery(
  EMAIL_FUNNELS_AUTO_REPLY_STATISTICS,
  variables
)

const emailAutoReplyStatisticsForCompany = computed(
  () => emailAutoReplyStatistics.value?.emailAutoReplyStatistics
)

const totalAutoReplies = ref(0)

watch(
  emailAutoReplyStatisticsForCompany,
  (val) => {
    if (val) {
      totalAutoReplies.value = val.totalCount ?? 0
    }
  },
  { immediate: true }
)

const isLoading = computed(
  () =>
    bounceStatisticsLoading.value ||
    sentCountsStatisticsLoading.value ||
    autoReplyStatisticsLoading.value
)

const handleSelect = (date) => {
  const chartDate = new Date(date)
  const formattedDate = chartDate.toISOString().split('T')[0]

  if (formattedDate === new Date(lastSelectedDate.value).toISOString().split('T')[0]) {
    totalSoftBounces.value = emailBounceStatistics.value?.emailBounceStatistics?.softBounce ?? 0
    totalHardBounces.value = emailBounceStatistics.value?.emailBounceStatistics?.hardBounce ?? 0
    totalAutoReplies.value = emailAutoReplyStatisticsForCompany.value?.totalCount ?? 0
    lastSelectedDate.value = null
    return
  }

  const selectedBar = emailBounceStatisticsForCompany?.value?.days?.find((item) => {
    const itemDate = new Date(item.date).toISOString().split('T')[0]
    return itemDate === formattedDate
  })

  const selectedBarAutoReplies = emailAutoReplyStatisticsForCompany?.value?.bounces?.filter(
    (item) => {
      const itemDate = new Date(item.date).toISOString().split('T')[0]
      return itemDate === formattedDate
    }
  )

  if (!selectedBar) {
    lastSelectedDate.value = formattedDate
  } else {
    lastSelectedDate.value = selectedBar.date
  }

  totalSoftBounces.value = selectedBar?.softBounce ?? 0
  totalHardBounces.value = selectedBar?.hardBounce ?? 0
  totalAutoReplies.value = selectedBarAutoReplies?.length ?? 0
}

const chartConfig = computed(() => {
  return createBarChartConfig({
    //@ts-expect-error:
    chartSeries: chartSeries,
    tooltipText: 'dashboard.details.sent.chart.tooltip',
    onSelect: handleSelect,
  })
})

const chartSeries = computed(() => {
  const dateFilters = dashboardContext.dateFilters.value

  let startDate: Date | undefined
  let endDate: Date | undefined

  if (dateFilters?.length === 2) {
    startDate = new Date(dateFilters[0])
    endDate = new Date(dateFilters[1])
  }

  return createBarChartSeries(
    emailSentCountsStatisticsForCompany.value?.days,
    'sent',
    startDate,
    endDate
  )
})

const pieChartConfig = computed(
  () =>
    ({
      chart: {
        type: 'pie',
        width: '100%',
      },
      labels: [
        t('dashboard.details.email-funnels.chart.label-one'),
        t('dashboard.details.email-funnels.chart.label-two'),
        t('dashboard.details.email-funnels.chart.label-three'),
      ],
      colors: ['#2AC5C0', '#25AEA9', '#217e7a'],
      dataLabels: {
        enabled: true,
        align: 'center',
        formatter: function (val: string | number | number[]) {
          const numeric = typeof val === 'number' ? val : Number(val)
          return Number.isFinite(numeric) ? `${numeric.toFixed(2)}%` : '0%'
        },
        style: {
          fontSize: '14px',
          fontWeight: 500,
        },
      },
      tooltip: {
        custom: function ({ series, seriesIndex, w }) {
          const label = w.config.labels[seriesIndex]
          const sent = series[seriesIndex]
          return `<div style="padding: 5px">
                ${label} - ${sent?.toLocaleString() || 0}
                </div>`
        },
      },
      legend: {
        fontSize: '14px',
        fontWeight: 500,
        position: 'right',
        markers: {
          size: 10,
        },
      },
      responsive: [
        {
          breakpoint: 680,
          options: {
            legend: {
              offsetY: 0,
              offsetX: 0,
              position: 'bottom',
            },
          },
        },
      ],
    }) as ApexOptions
)

const pieChartSeries = computed(() => {
  return [totalHardBounces?.value, totalSoftBounces?.value, totalAutoReplies?.value]
})
</script>

<style scoped>
.apex-chart {
  width: 60%;
}
.card-text-wrapper {
  border-radius: 10px;
  box-shadow: 0 11px 15px 0 rgba(0, 0, 0, 0.04);
}

.apexcharts-bar-series path {
  cursor: pointer;
}

.apex-chart-info {
  align-content: center;
}
@media (max-width: 960px) {
  .apex-chart {
    width: 100%;
  }
}
</style>
